import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Icon from 'components/commons/icon/Icon';
import SideMenu from 'components/features/side-menu/SideMenu';
import * as api from 'config/api';
import * as consts from 'config/consts';
import { formatDate } from 'utils/formatter';

import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';

import { useRouting } from 'hooks/useRouting';
import { useGlobalOneShot } from 'hooks/useGlobalOneShot';

//------------------------------------------------------------
// 日時比較して最新日時を返却
//------------------------------------------------------------
export function getLastDT(dt1: string, dt2: string): Date {
  const dDt1 = new Date(dt1);
  const dDt2 = new Date(dt2);
  return dDt1.getTime() >= dDt2.getTime() ? dDt1 : dDt2;
}

export type PingState = {
  shown: boolean;
  notifyType?: number;
};

type HeaderProps = {
  title: string;
  path: string;
  className: string | null;
  onNavLinkClick(e: React.MouseEvent, path: string): void;
};

const Header: React.SFC<HeaderProps> = props => {
  const location = useLocation();

  const { title, path, onNavLinkClick } = props;

  const routing = useRouting();

  const unmounted = useRef<boolean>(false);

  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [hideAnimate, setHideAnimate] = useState<boolean>(false);
  const [showPing, setShowPing] = useState<PingState>({ shown: false });

  const {
    customTitle,
    action,
    actionPattern,
    popBack,
    withBell: withBell_,
    withNavigation,
    withLogo,
    withBackIconWord,
    accountOrAuthenticationError,
    isReviseButtonActive,
  } = useCustomizeHeaderContext();
  const optonalAssetRegistrationOneShot = useGlobalOneShot(
    'optionalAssetRegistration'
  );
  const optonalAssetRenewalOneShot = useGlobalOneShot('optionalAssetRenewal');

  const menuShow = (): void => {
    setShowMenu(true);
    setHideAnimate(false);
  };
  const menuHide = (): void => {
    setShowMenu(false);
  };
  const menuHideAnimate = (): void => {
    setHideAnimate(true);
  };
  const goBack = (): void => {
    const back = popBack();
    if (back !== null) {
      if ('to' in back) routing.replaceRouting({ path: back.to }, true);
      if ('func' in back) back.func();
      return;
    }
    if (location.pathname === '/optionalassetregistration') {
      optonalAssetRegistrationOneShot.call(() => {
        routing.backRouting(true);
      });
      return;
    }
    if (location.pathname.startsWith('/optionalassetrenewal')) {
      optonalAssetRenewalOneShot.call(() => {
        routing.backRouting(true);
      });
      return;
    }
    routing.backRouting(true);
  };

  const goSecurityaccounts = (): void => {
    if (showMenu) {
      menuHideAnimate();
    }
    routing.pushRouting({ path: `/securityaccounts` }, true);
  };

  useEffect(() => {
    return () => {
      // unmount
      unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    // ホーム・取引履歴・資産推移・レポート・
    // 証券口座一覧・ポイント・お知らせ一覧の何れかを選択
    if (
      path === '/home' ||
      path === '/tradehistory' ||
      path === '/assettransition' ||
      path === '/report' ||
      path === '/target' ||
      path === '/securityaccounts' ||
      path === '/point' ||
      path === '/notificationList'
    ) {
      // お知らせ一覧の取得して、最新データの時刻を取得
      api.getNotifications().then(ret => {
        if (!unmounted.current && ret && ret.Items.length > 0) {
          // お知らせ一覧の最新の更新日時を取得
          const dtLatest = getLastDT(
            ret.Items[0].InsTime,
            ret.Items[0].UpdTime
          );
          const notifyType = ret.Items[0].Type;

          // お知らせ一覧を選択
          if (path === '/notificationList') {
            // →お知らせの最新日時をストレージに保存して、PingをOFF
            if (ret.Items.length > 0) {
              const setting = JSON.stringify({
                LastDateTime: formatDate(dtLatest, 'yyyy-MM-dd HH:mm:ss'),
              });
              localStorage.setItem('bell_state', setting);
              // PingをOFF
              setShowPing({ shown: false });
            }

            // お知らせ一覧以外を選択
          } else {
            // →ストレージに保存されていなければ、一度も、お知らせ一覧を開いていないので、PingをON
            // →最新のお知らせ一覧の時刻の方が、ストレージに保存された日時より新しいければ、PingをON
            const setting = localStorage.getItem('bell_state');
            if (setting === null) {
              setShowPing({ shown: true, notifyType: notifyType });
            } else {
              const dtStorage = new Date(JSON.parse(setting).LastDateTime);
              if (dtLatest.getTime() > dtStorage.getTime()) {
                setShowPing({ shown: true, notifyType: notifyType });
              }
            }
          }
        }
      });
    }
  }, [path]);

  const clsIcon = 'text-2xl text-gray-700';
  const clsPing = 'rounded-full h-2 w-2 bg-tradewell';
  const withBell =
    withBell_ ||
    path === '/tradehistory' ||
    path === '/personachart' ||
    path === '/assettransition' ||
    path === '/dailyreport' ||
    path === '/weeklyreport' ||
    path === '/monthlyreport' ||
    location.pathname === '/target' ||
    path === '/target' ||
    path === '/point' ||
    path === '/accountlinkageinfo';

  const getHeaderLeftActionButtonsElement = (
    path: string,
    id: string | undefined
  ): JSX.Element | null => {
    if (
      path === '/assettransition' ||
      path === '/tradedetail' ||
      withBackIconWord
    ) {
      return (
        <button className={`flex items-center py-2`} id={id} onClick={goBack}>
          <Icon name="chevron-left" className={clsIcon} />
          <div className="label">戻る</div>
        </button>
      );
    } else if (
      location.pathname.startsWith('/investor') &&
      path !== '/importantnotification'
    ) {
      return <></>;
    } else {
      return (
        <button
          className={`flex items-center py-2 ${withNavigation ? 'hidden' : ''}`}
          onClick={goBack}>
          <Icon name="chevron-left" className={clsIcon} />
        </button>
      );
    }
  };

  const getHeaderRightActionButtonsElement = (
    path: string
  ): [JSX.Element | null, { numberOfButton: number }] => {
    if (path.startsWith('/accountdetail')) {
      switch (actionPattern) {
        case consts.HeaderActionPatternType.編集_削除_1つ:
          return [
            <button
              className={`mx-2 flex flex-col items-center justify-center text-gray-700`}
              onClick={(): void => action(consts.HeaderActionType.編集)}>
              <Icon name="edit" className="text-lg" />
              <span className="text-sm">編集・削除</span>
            </button>,
            { numberOfButton: 1 },
          ];
        case consts.HeaderActionPatternType.編集_削除_それぞれ:
          return [
            <>
              <button
                className={`mx-2 flex flex-col items-center justify-center ${
                  isReviseButtonActive
                    ? 'text-gray-700 cursor-pointer'
                    : 'text-gray-300 cursor-default'
                }`}
                onClick={(): void => {
                  isReviseButtonActive && action(consts.HeaderActionType.編集);
                }}>
                <Icon name="edit" className="text-lg" />
                <span className="text-sm">編集</span>
              </button>
              <button
                className={`mx-2 flex flex-col items-center justify-center text-gray-700`}
                onClick={(): void => action(consts.HeaderActionType.削除)}>
                <Icon name="trash" className="text-lg" />
                <span className="text-sm">削除</span>
              </button>
            </>,
            { numberOfButton: 2 },
          ];
        case consts.HeaderActionPatternType.更新_削除_それぞれ:
          return [
            <>
              <button
                className={`mx-2 flex flex-col items-center justify-center ${
                  isReviseButtonActive
                    ? 'text-gray-700 cursor-pointer'
                    : 'text-gray-300 cursor-default'
                }`}
                onClick={(): void => {
                  isReviseButtonActive && action(consts.HeaderActionType.更新);
                }}>
                <Icon
                  name="reflesh"
                  className={`text-lg ${
                    isReviseButtonActive ? '' : 'text-gray-300'
                  }`}
                />
                <span className="text-sm">更新</span>
              </button>
              <button
                className={`mx-2 flex flex-col items-center justify-center text-gray-700`}
                onClick={(): void => action(consts.HeaderActionType.削除)}>
                <Icon name="trash" className="text-lg" />
                <span className="text-sm">削除</span>
              </button>
            </>,
            { numberOfButton: 2 },
          ];
        default:
          return [null, { numberOfButton: 2 }];
      }
    } else if (path.startsWith('/dividendedit')) {
      return [
        <button
          className={`mx-2 flex flex-col items-center justify-center text-gray-700`}
          onClick={(): void => action(consts.HeaderActionType.配当金)}>
          <Icon name="trash" className="text-lg" />
          <span className="text-sm">削除</span>
        </button>,
        { numberOfButton: 1 },
      ];
    } else {
      return [
        <>
          <div
            className={`flex items-center ${
              withNavigation ? '' : 'hidden'
            } cursor-pointer`}
            onClick={() => goSecurityaccounts()}>
            <img
              src="/static/images/securitiesButton.svg"
              width="50"
              height="30"
              alt="口座一覧へ"
            />
            <span
              className={`${clsPing} -mt-5 -ml-2 z-10 ${
                accountOrAuthenticationError ? '' : 'hidden'
              }`}
            />
          </div>
          <div className={`flex items-center`}>
            <section className={`pl-4 pr-2 pt-2`}>
              <div
                className={`btn-trigger ${showMenu ? 'active' : ''}`}
                id="btn01"
                onClick={!showMenu ? menuShow : menuHideAnimate}>
                <span />
                <span />
                <span />
              </div>
            </section>
            <span
              className={`${clsPing} -mt-6 -ml-3 z-10 ${
                showMenu ? 'hidden' : ''
              } ${showPing?.shown ? '' : 'hidden'}`}
            />
          </div>
        </>,
        { numberOfButton: (withBell ? 1 : 0) + (withNavigation ? 1 : 0) },
      ];
    }
  };

  const [
    headerRightActionButtons,
    { numberOfButton: numberOfHeaderRightActionButtons },
  ] = getHeaderRightActionButtonsElement(path);

  const pathRegex = /^\/target\/.*\/set-how-to-save\/(?<type>new|edit)$/;
  const result = pathRegex.exec(location.pathname);
  const id =
    result && result.groups !== undefined && result.groups.type === 'new'
      ? 'target_header_back'
      : undefined;

  return (
    <header className={`bg-gray-100 ${props.className}`} id="id_header">
      <nav className="container flex p-2">
        <div className="flex-1 flex justify-start" style={{ minWidth: '72px' }}>
          {getHeaderLeftActionButtonsElement(path, id)}
        </div>

        {path === '/home' ||
        (location.pathname.startsWith('/investor') &&
          path !== '/importantnotification') ? (
          <div className="flex justify-center items-center">
            <a title="みんかぶ" href="https://minkabu.jp/">
              <img
                src="https://assets.minkabu.jp/images/logo/v4/logo_minkabu.svg"
                width="110"
                height="30"
                alt="みんかぶ"
              />
            </a>
          </div>
        ) : path === '/accountlinkageinfo' || withLogo ? (
          <div className="flex-grow flex justify-center items-center">
            <img
              alt="みんかぶアセットプランナー"
              src="./static/images/minkabu_assetplanner.svg"
              style={{ height: '38px' }}
            />
          </div>
        ) : (
          <h1
            className={
              'font-bold text-center p-2' +
              (numberOfHeaderRightActionButtons >= 2
                ? ' truncate-420'
                : ' truncate')
            }>
            {customTitle ? customTitle : title}
          </h1>
        )}

        <div className="flex-1 flex justify-end" style={{ minWidth: '80px' }}>
          {headerRightActionButtons}
        </div>
      </nav>

      {showMenu && (
        <div className={`fixed left-0 ui-h-screen w-screen z-20`}>
          <SideMenu
            hideAnimate={hideAnimate}
            menuHide={menuHide}
            onNavLinkClick={onNavLinkClick}
            showPing={showPing}
          />
        </div>
      )}
    </header>
  );
};

export default Header;
